import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, CardBody, Card, Input, Label } from "reactstrap";
import { useSelector } from "react-redux";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import PCDatePicker from "../../Components/Common/BusinessMixDatePicker";
import GuestsInHouseChart from "./GuestsInHouseChart";
import HeatmapChart from "./BusinessMixHeatmap";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { convertDateFormate, getDateByName } from "../../helpers/api_helper";

const normalizeGuestsData = (captureGuestsData) => {
  if (Array.isArray(captureGuestsData)) return captureGuestsData;
  if (!captureGuestsData || typeof captureGuestsData !== "object") return [];

  const guestInHouse =
    captureGuestsData.guestInHouse || captureGuestsData.guest_in_house || [];
  const mealPeriods =
    captureGuestsData.capture_rate_by_meal_period ||
    captureGuestsData.captureRateByMealPeriod ||
    {};
  const periodRows = Object.values(mealPeriods).flat();
  const rowsByDate = new Map();

  guestInHouse.forEach((item) => {
    const date = item?.row_dt || item?.date;
    if (!date) return;
    rowsByDate.set(date, {
      date,
      guests_in_house: item.guest_in_house,
    });
  });

  periodRows.forEach((item) => {
    const date = item?.row_dt || item?.date;
    if (!date) return;
    const row = rowsByDate.get(date) || { date };
    const period = Object.entries(mealPeriods).find(([, rows]) =>
      rows.includes(item),
    )?.[0];
    if (period) {
      const periodName = period.toLowerCase() === "afternoon" ? "lunch" : period.toLowerCase();
      row[`${periodName}_percent`] = item.capture_rate;
    }
    rowsByDate.set(date, row);
  });

  return Array.from(rowsByDate.values());
};

const BusinessMix = () => {
  const [isSegment, setIsSegment] = useState(false);
  const [isFBRevenue, setIsFBRevenue] = useState(false);
  const [isOverall, setIsOverall] = useState(false);
  const [currency, setCurrency] = useState("$");
  const { currentOutlet } = useSelector((state) => ({
    currentOutlet: state.UserOutletGroup.currentOutlet,
  }));

  useEffect(() => {
    if (currentOutlet?.currency != "") {
      setCurrency(currentOutlet?.currency);
    }
  }, [currentOutlet]);
  const [businessMixData, setBusinessMixData] = useState({
    heatmapData: [],
    guestsData: [],
  });
  const [loader, setLoader] = useState(false);
  const [guestsLoader, setGuestsLoader] = useState(false);
  const requestIdRef = useRef(0);
  const guestsRequestIdRef = useRef(0);

  const [leftCustomDate, setLeftCustomDate] = useState([]);
  const [leftFilter, setLeftFilter] = useState({
    label: "Yesterday",
    value: "Yesterday",
  });

  const dateFilter =
    leftFilter.value === "Custom"
      ? {
          from: convertDateFormate(leftCustomDate[0]),
          to: convertDateFormate(leftCustomDate[1]),
        }
      : getDateByName(leftFilter.value);

  useEffect(() => {
    if (
      leftFilter.value === "Custom" &&
      (!leftCustomDate[0] || !leftCustomDate[1])
    ) {
      return;
    }

    const fetchGuestsChart = async () => {
      const requestId = ++guestsRequestIdRef.current;
      try {
        setGuestsLoader(true);
        setBusinessMixData((previous) => ({ ...previous, guestsData: [] }));
        const response = await postDataAPI({
          serviceName: "pms_data",
          op: "fetch_capture_ratio_guest_data",
          dateFilter,
        });
        if (requestId !== guestsRequestIdRef.current) return;

        const responseData = response?.data?.data ?? response?.data ?? {};
        const guestsPayload =
          responseData?.guestInHouse ||
          responseData?.guest_in_house ||
          responseData?.capture_rate_by_meal_period
            ? responseData
            : responseData?.result || responseData?.data || {};

        setBusinessMixData((previous) => ({
          ...previous,
          guestsData: normalizeGuestsData(guestsPayload),
        }));
      } catch (error) {
        if (requestId !== guestsRequestIdRef.current) return;
        console.error("GUESTS IN HOUSE API ERROR:", error);
        setBusinessMixData((previous) => ({ ...previous, guestsData: [] }));
      } finally {
        if (requestId === guestsRequestIdRef.current) setGuestsLoader(false);
      }
    };

    fetchGuestsChart();
  }, [leftFilter, leftCustomDate]);

  useEffect(() => {
    if (
      leftFilter.value === "Custom" &&
      (!leftCustomDate[0] || !leftCustomDate[1])
    ) {
      return;
    }

    const fetchBusinessMix = async () => {
      const requestId = ++requestIdRef.current;
      try {
        setLoader(true);
        setBusinessMixData((previous) => ({ ...previous, heatmapData: [] }));
        const response = await postDataAPI({
          serviceName: "matrix_data",
          op: "fetch_business_mix_data",
          dateFilter,
          bm_view: isSegment ? "segment" : "country",
          bm_matrix: isFBRevenue ? "fb_revenue" : "room_sold",
          show_top_ten: false,
        });

        const responseData = response?.data?.data ?? response?.data ?? {};
        if (requestId !== requestIdRef.current) return;
        const responseObject = Array.isArray(responseData)
          ? { heatmap_data: responseData }
          : responseData?.heatmap_data
          ? responseData
          : responseData?.data || {};

        setBusinessMixData((previous) => ({
          ...previous,
          heatmapData: Array.isArray(responseObject?.heatmap_data)
            ? responseObject.heatmap_data
            : [],
        }));
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("BUSINESS MIX API ERROR:", error);
        setBusinessMixData((previous) => ({ ...previous, heatmapData: [] }));
      } finally {
        if (requestId === requestIdRef.current) setLoader(false);
      }
    };

    fetchBusinessMix();
  }, [leftFilter, leftCustomDate, isSegment, isFBRevenue]);

  const selectedMetric = isFBRevenue ? "F&B_Revenue" : "Room_should";

  const selectedScale = isOverall ? "overall" : "daily";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Business Mix" pageTitle="Dashboard" />

          {/* FILTER CARD */}
          <Card className="p-0">
            <CardBody className="mb-1 py-2">
              <form>
                <Row className="align-items-center g-1">
                  {/* DATE */}
                  <Col className="mb-0" sm={12} lg={2}>
                    <PCDatePicker
                      activeDate={leftFilter}
                      setActiveDate={setLeftFilter}
                      customDate={leftCustomDate}
                      setCustomDate={setLeftCustomDate}
                      posEnd={false}
                    />
                  </Col>

                  {/* COUNTRY / SEGMENT */}
                  <Col className="mb-0 me-2" sm={6} lg="auto">
                    <div className="d-flex align-items-center gap-1 text-nowrap">
                      <Label className="small mb-0">View :</Label>

                      <span
                        className={`small ${!isSegment ? "" : "text-muted"}`}
                      >
                        Country
                      </span>

                      <div className="form-check form-switch form-switch-sm m-0">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          checked={isSegment}
                          onChange={(e) => setIsSegment(e.target.checked)}
                          style={{
                            width: "28px",
                            height: "16px",
                          }}
                        />
                      </div>

                      <span
                        className={`small ms-n1 ${
                          isSegment ? "" : "text-muted"
                        }`}
                      >
                        Segment
                      </span>
                    </div>
                  </Col>

                  {/* ROOMS / F&B */}
                  <Col className="mb-0 me-2" sm={6} lg="auto">
                    <div className="d-flex align-items-center gap-1 text-nowrap">
                      <span className="small mb-0">Metric :</span>

                      <span
                        className={`small ${!isFBRevenue ? "" : "text-muted"}`}
                      >
                        Rooms Sold
                      </span>

                      <div className="form-check form-switch form-switch-sm m-0">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          checked={isFBRevenue}
                          onChange={(e) => setIsFBRevenue(e.target.checked)}
                          style={{
                            width: "28px",
                            height: "16px",
                          }}
                        />
                      </div>

                      <span
                        className={`small ms-n1 ${
                          isFBRevenue ? "" : "text-muted"
                        }`}
                      >
                        F&B Revenue
                      </span>
                    </div>
                  </Col>

                  {/* DAILY / OVERALL */}
                  <Col className="mb-0" sm={6} lg="auto">
                    <div className="d-flex align-items-center gap-1 text-nowrap">
                      <span className="small mb-0">Heatmap Scale :</span>

                      <span
                        className={`small ${!isOverall ? "" : "text-muted"}`}
                      >
                        Daily
                      </span>

                      <div className="form-check form-switch form-switch-sm m-0">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          checked={isOverall}
                          onChange={(e) => setIsOverall(e.target.checked)}
                          style={{
                            width: "28px",
                            height: "16px",
                          }}
                        />
                      </div>

                      <span
                        className={`small ms-n1 ${
                          isOverall ? "" : "text-muted"
                        }`}
                      >
                        Overall
                      </span>
                    </div>
                  </Col>

                  {/* GRADIENT */}
                  <Col className="mb-0 ms-auto" lg="auto">
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small">Low</span>

                      <div
                        className="risk-gradient-bar"
                        style={{
                          width: "100px",
                          height: "8px",
                        }}
                      />

                      <span className="text-muted small">High</span>
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>

          {/* GUESTS CHART */}
          <GuestsInHouseChart
            data={businessMixData.guestsData}
            loader={guestsLoader}
          />

          {/* HEATMAP */}
            <HeatmapChart
              data={businessMixData.heatmapData}
              metric={selectedMetric}
              scale={selectedScale}
              view={isSegment ? "segments" : "countries"}
              loader={loader}
              currency={currency}
            />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BusinessMix;
